import { cloneElement, forwardRef, isValidElement, useId } from "react";
import type {
  InputHTMLAttributes,
  ReactElement,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  ReactNode,
} from "react";
import { cn } from "./cn";

const controlBase =
  "w-full rounded-control border bg-white text-ink-900 placeholder-ink-400 " +
  "px-4 py-2.5 text-base sm:text-sm transition-colors " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 focus:border-brand-600 " +
  "disabled:bg-surface-alt disabled:text-ink-400 disabled:cursor-not-allowed";

const controlNeutral = "border-line";
const controlError = "border-brand-bad focus:border-brand-bad focus-visible:ring-brand-bad/30";

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}

/** Label + hint + error wrapper for any control.
 *  Auto-associates the label with its child control (via a generated id) and
 *  wires aria-invalid / aria-describedby so screen readers announce the label,
 *  validation state, and hint/error text. */
export function Field({
  label,
  hint,
  error,
  required,
  htmlFor,
  children,
  className,
}: FieldProps) {
  const autoId = useId();
  const fieldId = htmlFor ?? autoId;
  const describedById = error
    ? `${fieldId}-error`
    : hint
      ? `${fieldId}-hint`
      : undefined;

  // Inject id + aria wiring into the single child control when possible.
  const control = isValidElement(children)
    ? cloneElement(children as ReactElement, {
        id: (children as ReactElement).props.id ?? fieldId,
        "aria-invalid": error
          ? true
          : (children as ReactElement).props["aria-invalid"],
        "aria-describedby":
          [
            (children as ReactElement).props["aria-describedby"],
            describedById,
          ]
            .filter(Boolean)
            .join(" ") || undefined,
      })
    : children;

  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <label
          htmlFor={fieldId}
          className="block text-xs font-semibold text-ink-600 uppercase tracking-wide"
        >
          {label}
          {required && <span className="text-brand-bad"> *</span>}
        </label>
      )}
      {control}
      {error ? (
        <p id={`${fieldId}-error`} className="text-xs text-brand-bad">⚠ {error}</p>
      ) : hint ? (
        <p id={`${fieldId}-hint`} className="text-xs text-ink-400">{hint}</p>
      ) : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cn(controlBase, invalid ? controlError : controlNeutral, className)}
      {...rest}
    />
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      className={cn(controlBase, "resize-none", invalid ? controlError : controlNeutral, className)}
      {...rest}
    />
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, className, children, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      className={cn(controlBase, "pr-8", invalid ? controlError : controlNeutral, className)}
      {...rest}
    >
      {children}
    </select>
  );
});
